import classNames from "classnames";
import uniqueId from "lodash/uniqueId";
import { useInputIcons } from "PFComponents/action_icons";
import { Flex } from "PFComponents/containers";
import { useModalContext } from "PFComponents/modal";
import { Tip } from "PFComponents/text/input_field_set/components/tip";
import { OptionOriginal } from "PFTypes/options";
import { FocusEvent, useCallback, useEffect, useRef, useState } from "react";

import { Dropdown, useDropdown } from "./dropdown";
import { Header } from "./header";
import { Popover } from "./popover";
import css from "./select_v2.module.scss";
import { SearchableVariant, SelectV2Props } from "./select_v2.types";
import { DROPDOWN_MAX_HEIGHT, getHighlightedOptionId } from "./select_v2.utils";
import { ValueBox } from "./value_box";
import { getKeyDownHandler } from "./value_box/get_key_down_handler";
import { ValueContainer } from "./value_box/value_container";

export const SelectV2 = <T extends OptionOriginal = OptionOriginal>({
  id,
  searchId,
  tip,
  inline,
  label,
  labelIcons,
  placeholder,
  value,
  onRestore,
  onClear,
  onChange,
  onCreateOption,
  createOptionBlocklist,
  onDropdownClose,
  options,
  fetchOptions,
  parseOptions,
  multiple,
  searchableVariant = SearchableVariant.Dynamic,
  focusSearchInputOnOpen = true,
  renderDisplayValue,
  cacheTime = 300000,
  disabled,
  locked,
  lockedTip,
  required,
  displayValueBelow,
  dropdownId: externalDropdownId,
  isFetchingSelectedOptions: isFetchingSelectedOptionsProp,
  restoreLabel,
  error,
  warning,
  dropdownWidth,
  dropdownMaxHeight = DROPDOWN_MAX_HEIGHT,
  valueBoxWidth,
  limit,
  qaId,
  title,
  className,
  style,
  tabIndex,
  ref,
  inputRef,
  clipOptionsOverflow = true,
  noOptionsText,
  dropdownHeaderElement,
  noPortal,
  createButton,
  portalRef: propPortalRef,
  variant = "default"
}: SelectV2Props<T>) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const containerRef = ref ?? innerRef;
  const innerValueBoxRef = useRef<HTMLDivElement>(null);
  const valueBoxRef = inputRef ?? innerValueBoxRef;
  const localDropdownIdRef = useRef<string>(uniqueId("select-dropdown"));
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // prevents closing the dropdown when clicking on the action icons
  const handleIconMouseDown = useCallback(
    (event: React.MouseEvent<SVGElement>) => {
      if (containerRef?.current?.contains(document.activeElement)) {
        event.preventDefault();
      }
    },
    [containerRef]
  );

  const actionIcons = useInputIcons({
    onRestore,
    onClear,
    label,
    locked,
    disabled,
    restoreLabel,
    onMouseDown: handleIconMouseDown
  });
  const { portalRef: modalPortalRef } = useModalContext();

  const dropdownId = externalDropdownId ?? localDropdownIdRef.current;
  const portalRef = noPortal ? undefined : (propPortalRef ?? modalPortalRef);

  const handleOpen = useCallback(
    (value: boolean) => {
      setIsOpen(value);
      value === false && onDropdownClose?.();
    },
    [onDropdownClose]
  );

  const dropdownData = useDropdown({
    dropdownId,
    searchId,
    isOpen,
    setIsOpen: handleOpen,
    value,
    triggerRef: valueBoxRef,
    options,
    fetchOptions,
    parseOptions,
    multiple,
    onChange,
    onCreateOption,
    createOptionBlocklist,
    searchableVariant,
    limit,
    cacheTime
  });

  useEffect(() => {
    if (isOpen && disabled) {
      handleOpen(false);
    }
  }, [disabled, handleOpen, isOpen]);

  const { highlightedIndex, resultOptions, selectedOptions, isFetchingSelectedOptions, handleKeyDown } =
    dropdownData;

  const isFetchingSelectedOptionsFinal = isFetchingSelectedOptionsProp ?? isFetchingSelectedOptions;

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const { currentTarget, relatedTarget } = event;

      const popoverInPortal = portalRef?.current
        ? Array.from(portalRef.current.children).find((child) => child.hasAttribute("data-popover"))
        : undefined;

      if (
        relatedTarget &&
        (currentTarget.contains(relatedTarget) ||
          containerRef.current?.contains(relatedTarget) ||
          popoverInPortal?.contains(relatedTarget))
      ) {
        if (
          relatedTarget.getAttribute("role") === "option" ||
          relatedTarget.getAttribute("role") === "listbox" ||
          relatedTarget.getAttribute("type") === "checkbox"
        ) {
          currentTarget.focus({ preventScroll: true });
        }
        return;
      }
      handleOpen(false);
    },
    [handleOpen, portalRef, containerRef]
  );

  const { onKeyDown: handleDropdownOpenStateOnKeyDown } = getKeyDownHandler({ setIsOpen: handleOpen });
  const onValueBoxKeyDown = isOpen ? handleKeyDown : handleDropdownOpenStateOnKeyDown;

  const handleValueBoxClick = () => {
    valueBoxRef.current?.focus();
    handleOpen(!isOpen);
  };

  const hasTip = !!(tip || error || warning);
  const dropdownMinWidth = containerRef.current?.offsetWidth ?? 0;

  return (
    <div
      id={id}
      className={classNames(css.root, { [css.tableVariant]: variant === "table" }, className)}
      style={style}
      title={title}
      data-qa-id={qaId}
    >
      <Flex
        ref={containerRef}
        gap={inline ? "spacingMd" : "spacingSm"}
        direction={inline ? "row" : "column"}
        className={classNames({ [css.tableVariant]: variant === "table" })}
      >
        <Header
          label={label}
          labelIcons={labelIcons}
          actionIcons={actionIcons}
          required={required}
          disabled={disabled}
        />
        <ValueBox<T>
          ref={valueBoxRef}
          selectedOptions={selectedOptions}
          isFetching={isFetchingSelectedOptionsFinal}
          label={label || title}
          placeholder={placeholder}
          multiple={multiple}
          renderDisplayValue={renderDisplayValue}
          isOpen={isOpen}
          onKeyDown={onValueBoxKeyDown}
          onClick={handleValueBoxClick}
          onChange={onChange}
          onBlur={handleBlur}
          actionIcons={!label ? actionIcons : undefined}
          dropdownId={dropdownId}
          highlightedId={getHighlightedOptionId(highlightedIndex, resultOptions, dropdownId)}
          locked={locked}
          lockedTip={lockedTip}
          disabled={disabled}
          displayValueBelow={displayValueBelow}
          required={required}
          isError={!!error}
          variant={variant}
          tabIndex={tabIndex}
          valueBoxWidth={valueBoxWidth}
        />
        {hasTip && variant !== "table" && <Tip tip={tip} error={error} warning={warning} />}
        {isOpen && !disabled && !locked && (
          <Popover reference={containerRef} portalRef={portalRef} qaId={qaId ? `${qaId}Popover` : undefined}>
            <Dropdown<T>
              style={{
                width: dropdownWidth ?? dropdownMinWidth,
                minWidth: dropdownMinWidth,
                maxHeight: dropdownMaxHeight
              }}
              dropdownId={dropdownId}
              clipOptionsOverflow={clipOptionsOverflow}
              noOptionsText={noOptionsText}
              dropdownHeaderElement={dropdownHeaderElement}
              value={value}
              multiple={multiple}
              onBlur={handleBlur}
              dropdownData={dropdownData}
              createButton={createButton}
              searchInputQaId={qaId ? `${qaId}SearchInput` : undefined}
              focusSearchInputOnOpen={focusSearchInputOnOpen}
            />
          </Popover>
        )}
      </Flex>
      {displayValueBelow && selectedOptions.length > 0 && (
        <ValueContainer
          onChange={onChange}
          renderDisplayValue={renderDisplayValue}
          multiple={multiple}
          selectedOptions={selectedOptions}
          className={css.valueContainer}
          isFetching={isFetchingSelectedOptionsFinal}
          locked={locked}
          disabled={disabled}
        />
      )}
    </div>
  );
};
